import { splitEssenceTitle } from "@/lib/essences/title";
import { cn } from "@/lib/utils";

type HeadingTag = "h1" | "h2" | "h3" | "span";

interface EssenceTitleProps {
  title: string;
  as?: HeadingTag;
  size?: "card" | "compact" | "modal" | "page";
  className?: string;
  id?: string;
}

/**
 * The signature title treatment: the near-universal "Essence of the" prefix
 * becomes a quiet eyebrow and the subject carries the display type. The
 * heading still reads as the full title to assistive technology.
 */
export function EssenceTitle({
  title,
  as = "h3",
  size = "card",
  className,
  id,
}: EssenceTitleProps) {
  const Tag = as;
  const { prefix, subject } = splitEssenceTitle(title);

  const subjectClass =
    size === "page"
      ? "font-display text-[clamp(1.75rem,1.2rem+2vw,2.5rem)] font-semibold leading-[1.12] tracking-[-0.02em] text-ink text-balance"
      : size === "modal"
        ? "font-display text-[clamp(1.35rem,1rem+1.4vw,1.9rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-ink text-balance"
        : size === "compact"
          ? "font-display text-[15px] font-semibold leading-snug tracking-[-0.01em] text-ink"
          : "font-display text-[17px] font-semibold leading-[1.3] tracking-[-0.01em] text-ink line-clamp-2";

  const prefixClass =
    size === "page"
      ? "micro-label mb-2 block text-[12px] text-accent/80"
      : size === "modal"
        ? "micro-label mb-1.5 block text-accent/80"
        : size === "compact"
          ? "micro-label mb-0.5 block text-[10px]"
          : "micro-label mb-1 block";

  return (
    <Tag id={id} className={cn("min-w-0", className)}>
      {prefix && <span className={prefixClass}>{prefix}</span>}
      <span className={cn("block", subjectClass)}>{subject}</span>
    </Tag>
  );
}
